import * as React from "react";
import {AdminRegion} from "../../../model/RegionLoginContent.model";

export interface IAdminLoginContentUnpublishedSummaryProps {
    regions: AdminRegion[];
    onRegionSelect: (region: AdminRegion) => void;
}

export const UNPUBLISHED_SUMMARY_NEW_CHIP = "New — Never Published";

export enum SectionLabel {
    HERO = "Hero Content",
    CAROUSEL = "Carousel Cards",
    PILLS = "Service Pills",
    STATS = "Stats Bar",
}

export const AdminLoginContentUnpublishedSummary: React.FunctionComponent<IAdminLoginContentUnpublishedSummaryProps> =
    (props: IAdminLoginContentUnpublishedSummaryProps) => {
        const unpublishedRegions = props.regions.filter(region => !region.published);
        const unpublishedSummaryTitle = (count: number): string =>
            `${count} unpublished region${count === 1 ? "" : "s"}`;
        const isNeverPublished = (region: AdminRegion): boolean => !region.publishedAt;
        const changeChipLabel = (section: string): string =>
            `${SectionLabel[section as keyof typeof SectionLabel] ?? section} changed`;

        if (unpublishedRegions.length === 0) {
            return null;
        }

        const renderRegionChanges = (region: AdminRegion) => {
            if (isNeverPublished(region)) {
                return (
                    <span className="admin-login-content__global-draft-chip admin-login-content__global-draft-chip--new">
                        {UNPUBLISHED_SUMMARY_NEW_CHIP}
                    </span>
                );
            }
            return (region.changedSections ?? []).map(section => (
                <span key={section} className="admin-login-content__global-draft-chip">
                    {changeChipLabel(section)}
                </span>
            ));
        };

        const renderUnpublishedRegion = (region: AdminRegion) => (
            <div key={region.id} className="admin-login-content__global-draft-row">
                <button
                    type="button"
                    className="admin-login-content__global-draft-region"
                    onClick={() => props.onRegionSelect(region)}>
                    <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    {region.regionName}
                </button>
                <div className="admin-login-content__global-draft-changes">
                    {renderRegionChanges(region)}
                </div>
                {region.lastEditedBy && (
                    <span className="admin-login-content__global-draft-editor">
                        by {region.lastEditedBy}
                    </span>
                )}
            </div>
        );

        return (
            <div
                className="admin-login-content__global-draft-summary"
                data-testid="admin-login-content__unpublished-summary">
                <div className="admin-login-content__global-draft-header">
                    <i className="fa-solid fa-file-pen" aria-hidden="true" />
                    <strong>{unpublishedSummaryTitle(unpublishedRegions.length)}</strong>
                </div>
                <div className="admin-login-content__global-draft-list">
                    {unpublishedRegions.map(renderUnpublishedRegion)}
                </div>
            </div>
        );
    };
